import React, { type ReactNode } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSelector from "./LanguageSelector";
import { usePublicPageMeta } from "../hooks/usePublicPageMeta";

type PublicPageFrameProps = {
  title: string;
  subtitle?: ReactNode;
  seoTitle: string;
  seoDescription: string;
  children: ReactNode;
  updatedAtLabel?: string;
  updatedAt?: string;
  homeLabel: string;
  homeHref: string;
  footerLinks?: Array<{
    label: string;
    href: string;
  }>;
};

const PublicPageFrame: React.FC<PublicPageFrameProps> = ({
  title,
  subtitle,
  seoTitle,
  seoDescription,
  children,
  updatedAtLabel,
  updatedAt,
  homeLabel,
  homeHref,
  footerLinks = [],
}) => {
  const { language, t } = useLanguage();

  usePublicPageMeta({
    language,
    title: seoTitle,
    description: seoDescription,
    imageTitle: seoTitle,
    imageDescription: seoDescription,
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-950 text-slate-100">
      <div className="relative isolate">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-[-8%] top-[-6%] h-[30rem] w-[30rem] rounded-full bg-cyan-500/12 blur-3xl" />
          <div className="absolute right-[-6%] top-[10%] h-[28rem] w-[28rem] rounded-full bg-violet-500/10 blur-3xl" />
        </div>

        <main className="relative mx-auto w-full max-w-3xl px-6 py-10 sm:px-8 lg:py-14">
          <header className="mb-10 flex flex-col gap-4 rounded-2xl border border-slate-800/80 bg-slate-900/50 px-5 py-4 backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between">
            <a href="/" className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/20 bg-slate-900/80">
                <img
                  src="/ortho-logo.png"
                  alt="OrthoPrecision"
                  className="h-8 w-8 object-contain drop-shadow-[0_0_12px_rgba(34,211,238,0.35)]"
                />
              </div>
              <span className="text-base font-semibold tracking-tight text-white">
                OrthoPrecision
              </span>
            </a>

            <div className="flex flex-wrap items-center gap-3">
              <LanguageSelector variant="full" />
              <a
                href={homeHref}
                className="text-sm font-medium text-slate-400 transition-colors hover:text-cyan-300"
              >
                {homeLabel}
              </a>
            </div>
          </header>

          <article className="rounded-[2rem] border border-slate-800/80 bg-slate-900/45 p-6 backdrop-blur-xl sm:p-8 lg:p-10">
            <h1 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              {title}
            </h1>

            {subtitle && (
              <div className="mt-3 text-sm leading-6 text-slate-400 sm:text-base">
                {subtitle}
              </div>
            )}

            {updatedAtLabel && updatedAt && (
              <p className="mt-2 text-sm text-slate-400">
                {updatedAtLabel}: {updatedAt}
              </p>
            )}

            <div className="mt-6 space-y-4 text-base leading-7 text-slate-300">
              {children}
            </div>
          </article>

          <footer className="mt-8 flex flex-col items-center gap-4 text-center">
            {footerLinks.length > 0 && (
              <nav className="flex flex-wrap items-center justify-center gap-4 text-xs font-medium text-slate-500">
                {footerLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="transition-colors hover:text-cyan-300"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
            )}
            <p className="text-xs text-slate-500">
              {t("publicCopyright", { year: new Date().getFullYear() })}
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default PublicPageFrame;
